import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  getKeyValue,
} from "@nextui-org/react";
import { useEffect, useState } from "react";
import { addDays, format, parse } from "date-fns";
import { cn } from "@/lib/utils";
import { useGetGgrRevenue } from "@/services/game.service";
import { getLocalTimeZone } from "@internationalized/date";
import { DateRangePicker } from "@/components/ui/daterangepicker";

interface RevenueData {
  category: string;
  ggrShare: number;
  group: string;
  groupId: number;
  name: string;
  platformShare: number;
  providerShare: number;
  revenue: number;
}

const RevenueList = () => {
  const [tableRows, setTableRows] = useState<RevenueData[]>([]);
  const [date, setDate] = useState({
    from: addDays(new Date(), -7),
    to: new Date(),
  });

  const { data, isSuccess, isFetching, refetch } = useGetGgrRevenue(
    date?.from ?? new Date(),
    date?.to ?? new Date(),
  );

  useEffect(() => {
    if (isSuccess) {
      setTableRows(data.data);
    }
  }, [isSuccess]);

  const onApplyFilter = () => {
    refetch();
  };

  const columns = [
    {
      key: "group",
      label: "Group",
    },
    {
      key: "name",
      label: "Name",
    },
    {
      key: "category",
      label: "Category",
    },
    {
      key: "revenue",
      label: "Revenue",
    },
    {
      key: "ggrShare",
      label: "Share Percentage",
    },
    {
      key: "providerShare",
      label: "Provider Share",
    },
    {
      key: "platformShare",
      label: "Platform Share",
    },
  ];

  const totalReinvestmentColumns = [
    {
      key: "total",
      label: "Total Revenue",
    },
    {
      key: "providerShare",
      label: "Provider Share",
    },
    {
      key: "platformShare",
      label: "Platform Share",
    },
    {
      key: "fixedExpenses",
      label: "Fixed Expenses",
    },
    {
      key: "burn",
      label: "Burn",
    },
    {
      key: "tournamentsPot",
      label: "Tournaments Pot",
    },
    {
      key: "challengesPot",
      label: "Challenges Pot",
    },
    {
      key: "dailyBonusPot",
      label: "Daily Bonus Pot",
    },
    {
      key: "weeklyBonusPot",
      label: "Weekly Bonus Pot",
    },
    {
      key: "monthlyBonusPot",
      label: "Monthly Bonus Pot",
    },
    {
      key: "rakeback",
      label: "Cash BackBonus Pot",
    },
    {
      key: "affiliate",
      label: "Affiliate And Referral",
    },
  ];

  return (
    <div>
      <div>
        <div className="font-ubuntu float-left text-center text-2xl  text-sm font-semibold text-white sm:text-lg">
          <p className="hidden sm:block">Revenue List</p>
        </div>
        <div className="flex w-full flex-col gap-4">
          <div className="mb-6 mt-5 flex w-auto flex-wrap items-start gap-4 md:mb-0 md:flex-nowrap">
            <div className={cn("grid gap-2")}>
              <DateRangePicker
                onChange={(e: any) => {
                  const startDate = format(
                    e.start.toDate(getLocalTimeZone()),
                    "yyyy-MM-dd",
                  );
                  const endDate = format(
                    e.end.toDate(getLocalTimeZone()),
                    "yyyy-MM-dd",
                  );

                  setDate({
                    from: parse(startDate, "yyyy-MM-dd", new Date()),
                    to: parse(endDate, "yyyy-MM-dd", new Date()),
                  });
                }}
              />
            </div>
            <Button
              className="admin-common-button-gradient font-ubuntu w-40 rounded-md text-base font-bold"
              onPress={onApplyFilter}
            >
              Apply
            </Button>
            <div className="float-right ml-96 ms-auto w-1/2"></div>
          </div>
        </div>
        {
          <div className="w-full sm:mt-5">
            <div className="w-full overflow-x-auto">
              <Table
                removeWrapper
                className="table-striped table-striped-even table"
              >
                <TableHeader className="rounded-none" columns={columns}>
                  {(column) => (
                    <TableColumn key={column.key}>
                      <div className="white-op flex">
                        {column.label}
                        <Button
                          isIconOnly
                          className="transparent-bg h-auto w-auto"
                        ></Button>
                      </div>
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody
                  items={tableRows}
                  loadingContent={"Loading data..."}
                  isLoading={isFetching}
                >
                  {(item) => (
                    <TableRow key={item.name}>
                      {(columnKey) => (
                        <TableCell>
                          {columnKey === "no" ? (
                            getKeyValue(item, columnKey) + "."
                          ) : columnKey === "group" ? (
                            <div>{getKeyValue(item, columnKey)}</div>
                          ) : columnKey === "name" ? (
                            <div>{getKeyValue(item, columnKey)}</div>
                          ) : columnKey === "category" ? (
                            <div>{getKeyValue(item, columnKey)}</div>
                          ) : columnKey === "revenue" ? (
                            <div>
                              {getKeyValue(item, columnKey).toLocaleString(
                                undefined,
                                { maximumFractionDigit: 2 },
                              )}
                            </div>
                          ) : columnKey === "ggrShare" ? (
                            <div>{getKeyValue(item, columnKey)}</div>
                          ) : columnKey === "providerShare" ? (
                            <div>
                              {getKeyValue(item, columnKey).toLocaleString(
                                undefined,
                                { maximumFractionDigit: 2 },
                              )}
                            </div>
                          ) : columnKey === "platformShare" ? (
                            <div>
                              {getKeyValue(item, columnKey).toLocaleString(
                                undefined,
                                { maximumFractionDigit: 2 },
                              )}
                            </div>
                          ) : (
                            <div>
                              {getKeyValue(item, columnKey).toLocaleString(
                                undefined,
                                { maximumFractionDigit: 2 },
                              )}
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        }

        {
          <div className="w-full sm:mt-5">
            <div className="w-full overflow-x-auto">
              <Table
                removeWrapper
                className="table-striped table-striped-even table"
              >
                <TableHeader
                  className="rounded-none"
                  columns={totalReinvestmentColumns}
                >
                  {(column) => (
                    <TableColumn key={column.key}>
                      <div className="white-op flex">
                        {column.label}
                        <Button
                          isIconOnly
                          className="transparent-bg h-auto w-auto"
                        ></Button>
                      </div>
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody items={[data?.share]}>
                  {(item) => (
                    <TableRow key={item?.total}>
                      {(columnKey) => (
                        <TableCell>
                          {columnKey === "no" ? (
                            getKeyValue(item, columnKey) + "."
                          ) : columnKey === "group" ? (
                            <div>{getKeyValue(item, columnKey)}</div>
                          ) : columnKey === "name" ? (
                            <div>{getKeyValue(item, columnKey)}</div>
                          ) : columnKey === "category" ? (
                            <div>{getKeyValue(item, columnKey)}</div>
                          ) : columnKey === "revenue" ? (
                            <div>
                              {getKeyValue(item, columnKey).toLocaleString(
                                undefined,
                                { maximumFractionDigit: 2 },
                              )}
                            </div>
                          ) : columnKey === "ggrShare" ? (
                            <div>{getKeyValue(item, columnKey)}</div>
                          ) : columnKey === "providerShare" ? (
                            <div>
                              {getKeyValue(item, columnKey).toLocaleString(
                                undefined,
                                { maximumFractionDigits: 2 },
                              )}
                            </div>
                          ) : columnKey === "platformShare" ? (
                            <div>
                              {getKeyValue(item, columnKey).toLocaleString(
                                undefined,
                                { maximumFractionDigits: 2 },
                              )}
                            </div>
                          ) : (
                            <div>
                              {getKeyValue(item, columnKey).toLocaleString(
                                undefined,
                                { maximumFractionDigits: 2 },
                              )}
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        }
      </div>
    </div>
  );
};

export default RevenueList;
